import { useCountAllRelatedFields } from "@/api/related-fields";
import { Tirage } from "@/api/tirages/tirage.api";
import { ConfigurationTypeFile } from "@/config/forms/general";
import {
  Artwork,
  Auction,
  Audiovisual,
  Author,
  Bibliography,
  Collector,
  Document,
  Exhibition,
  Multipleprint,
  Photography,
  Writing,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ArtworkInAuctionLinks from "./links/ArtworkInAuction";
import ArtworkInBibliographyLinks from "./links/ArtworkInBibliography";
import ArtworkInExhibitions from "./links/ArtworkInExhibitions";
import ArtworkLinks from "./links/ArtworkLinks";
import AuctionLinks from "./links/AuctionLinks";
import AudiovisualLinks from "./links/AudiovisualLinks";
import AuthorLinks from "./links/AuthorLinks";
import BibliographyLinks from "./links/BibliographyLinks";
import CollectorInMultipleprints from "./links/CollectorInMultipleprint";
import DocumentInBibliographyLinks from "./links/DocumentInBibliography";
import DocumentLinks from "./links/DocumentLinkts";
import ExhibitionLinks from "./links/ExhibitionLinks";
import MultipleLinks from "./links/MultipleLinks";
import MultipleprintInAuctionLinks from "./links/MultipleprintInAuctionLinks";
import MultipleprintInBibliographyLinks from "./links/MultipleprintInBibliography";
import MultipleprintInExhibtionLinks from "./links/MultipleprintInExhibtionLinks";
import PhotographiesLinks from "./links/PhotographiesLinks";
import PhotographyInBibliographyLinks from "./links/PhotographyInBibliography";
import TirageInAuctionLinks from "./links/TirageInAuction";
import TirageInBibliographyLinks from "./links/TirageInBibliography";
import TirageInExhibitionLinks from "./links/TirageInExhibition";
import TirageLinks from "./links/TirageLinks";
import WritingLinks from "./links/WritingLinks";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export type LinkableData =
  | Artwork
  | Multipleprint
  | Photography
  | Audiovisual
  | Document
  | Writing
  | Author
  | Exhibition
  | Auction
  | Collector
  | Bibliography
  | Tirage;
export type LinkableType =
  | "artwork"
  | "multipleprint"
  | "photography"
  | "audiovisual"
  | "document"
  | "exhibition"
  | "bibliography"
  | "author"
  | "auction"
  | "collector"
  | "writing"
  | "tirage";

interface LinksManagementProps {
  id?: string;
  configuration: ConfigurationTypeFile | null | undefined;
  data: LinkableData;
  type: LinkableType;
  className?: string;
}

interface TabData {
  name: string;
  label: string;
}

const LinksManagement = ({
  id,
  data,
  configuration,
  type,
  className,
}: LinksManagementProps) => {
  const config = configuration?.fields.find((field) => field.type === "links");
  const { isLoading, data: countRelatedFields } = useCountAllRelatedFields(
    id,
    type,
    config?.links?.map((link) => link.name) ?? [],
  );
  const links = config?.links?.filter((link) => !link.hidden);

  const defaultTab = useMemo(() => links?.[0]?.name, [links]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchParams, setSearchParam] = useSearchParams();

  useEffect(() => {
    const view = searchParams.get("linkTab");

    if (view == null) {
      setActiveTab(defaultTab);
      return;
    }
    if (links?.map((t) => t.name).includes(view)) {
      setActiveTab(view);
      return;
    }
  }, [searchParams, setActiveTab, links, defaultTab]);

  const components = useMemo(() => {
    switch (activeTab) {
      case "relatedArtwork":
        return <ArtworkLinks id={id ?? ""} type={type} />;
      case "relatedAudiovisual":
        return <AudiovisualLinks id={id ?? ""} type={type} />;
      case "relatedDocument":
        return <DocumentLinks id={id ?? ""} type={type} />;
      case "relatedWriting":
        return <WritingLinks id={id ?? ""} type={type} />;
      case "relatedPhotography":
        return <PhotographiesLinks id={id ?? ""} type={type} />;
      case "relatedMultipleprint":
        return <MultipleLinks id={id ?? ""} type={type} />;
      case "relatedBibliography":
        return <BibliographyLinks id={id ?? ""} type={type} />;
      case "relatedAuction":
        return <AuctionLinks id={id ?? ""} type={type} />;
      case "relatedAuthor":
        return <AuthorLinks id={id ?? ""} type={type} />;
      case "relatedExhibition":
        return <ExhibitionLinks id={id ?? ""} type={type} />;
      case "relatedTirage":
        return <TirageLinks id={id ?? ""} type={type} />;
      case "collectorInMultipleprints":
        return (
          <CollectorInMultipleprints
            id={id ?? ""}
            type={type}
            collector={data as Collector}
            fields={
              links?.find((link) => link.name === "collectorInMultipleprints")
                ?.fields
            }
          />
        );
      case "multipleprintInBibliography":
        return (
          <MultipleprintInBibliographyLinks
            id={id ?? ""}
            type={type}
            multipleprint={data as Multipleprint}
            fields={
              links?.find((link) => link.name === "multipleprintInBibliography")
                ?.fields
            }
          />
        );
      case "multipleprintInExhibition":
        return (
          <MultipleprintInExhibtionLinks
            id={id ?? ""}
            type={type}
            multipleprint={data as Multipleprint}
            fields={
              links?.find((link) => link.name === "multipleprintInExhibition")
                ?.fields
            }
          />
        );
      case "multipleprintInAuction":
        return (
          <MultipleprintInAuctionLinks
            id={id ?? ""}
            type={type}
            multipleprint={data as Multipleprint}
            fields={
              links?.find((link) => link.name === "multipleprintInAuction")
                ?.fields
            }
          />
        );
      case "documentInBibliography":
        return (
          <DocumentInBibliographyLinks
            id={id ?? ""}
            type={type}
            document={data as Document}
            bibliography={data as Bibliography}
            fields={
              links?.find((link) => link.name === "documentInBibliography")
                ?.fields
            }
          />
        );
      case "photographyInBibliography":
        return (
          <PhotographyInBibliographyLinks
            id={id ?? ""}
            type={type}
            photography={data as Photography}
            bibliography={data as Bibliography}
            fields={
              links?.find((link) => link.name === "photographyInBibliography")
                ?.fields
            }
          />
        );
      case "artworkInExhibitions":
        return (
          <ArtworkInExhibitions
            id={id ?? ""}
            artwork={data as Artwork}
            exhibition={data as Exhibition}
            type={type}
            fields={
              links?.find((link) => link.name === "artworkInExhibitions")
                ?.fields
            }
          />
        );
      case "artworkInBibliography":
        return (
          <ArtworkInBibliographyLinks
            id={id ?? ""}
            artwork={data as Artwork}
            bibliography={data as Bibliography}
            type={type}
            fields={
              links?.find((link) => link.name === "artworkInBibliography")
                ?.fields
            }
          />
        );
      case "artworkInAuctions":
        return (
          <ArtworkInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={data as Auction}
            artwork={data as Artwork}
            fields={
              links?.find((link) => link.name === "artworkInAuctions")?.fields
            }
          />
        );
      case "tirageInExhibition":
        return (
          <TirageInExhibitionLinks
            id={id ?? ""}
            tirage={data as Tirage}
            exhibition={data as Exhibition}
            type={type}
            fields={
              links?.find((link) => link.name === "tirageInExhibition")?.fields
            }
          />
        );
      case "tirageInAuction":
        return (
          <TirageInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={data as Auction}
            tirage={data as Tirage}
            fields={
              links?.find((link) => link.name === "tirageInAuction")?.fields
            }
          />
        );
      case "tirageInBibliography":
        return (
          <TirageInBibliographyLinks
            id={id ?? ""}
            tirage={data as Tirage}
            bibliography={data as Bibliography}
            type={type}
            fields={
              links?.find((link) => link.name === "tirageInBibliography")
                ?.fields
            }
          />
        );
      default:
        return null;
    }
  }, [activeTab, data, id, links, type]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(val) => {
        setActiveTab(val);
        searchParams.set("linkTab", val);
        setSearchParam(searchParams);
      }}
      className={cn("w-full pr-10", className)}
    >
      <TabsList>
        {links?.map((tab: TabData) => (
          <TabsTrigger
            key={tab.name}
            value={tab.name}
            className="flex items-center gap-2 pr-2"
          >
            {tab.label}
            {!isLoading && (
              <Badge
                variant={activeTab === tab.name ? "outlinedActive" : "outlined"}
                className="w-6 h-6"
              >
                {countRelatedFields?.[tab.name]}
              </Badge>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {links?.map((tab: TabData) => (
        <TabsContent
          key={tab.name}
          value={tab.name}
          className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
        >
          {components}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default LinksManagement;
